import React, { useState, useRef } from "react";
import { useMutation } from "graphql-hooks";
import {
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Box,
  TextField,
  Divider,
  InputAdornment,
  Tooltip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { SHARE_VIA_EMAIL_MUTATION } from "../graphQL/share_qry";

// Social media icons
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import LinkIcon from "@mui/icons-material/Link";

// Import sample Avatar images
import Avatar1 from "../assets/SearchResults/Avatars/James_Anderson.png";
import Avatar2 from "../assets/SearchResults/Avatars/David_Miller.png";
import Avatar3 from "../assets/SearchResults/Avatars/Robert_Thompson.png";
import Avatar4 from "../assets/SearchResults/Avatars/Mary_Robinson.png";
import Avatar5 from "../assets/SearchResults/Avatars/Michael_Harris.png";
import Avatar6 from "../assets/SearchResults/Avatars/Michael_Harris_2.png";
import Avatar7 from "../assets/SearchResults/Avatars/Sarah_White.png";
import Avatar8 from "../assets/SearchResults/Avatars/Jennifer_Taylor.png";

// React Share imports
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
} from "react-share";

// Sample user data - replace with actual data
const SAMPLE_USERS = [
  { id: 1, name: "James Anderson", avatar: Avatar1 },
  { id: 2, name: "Emily Johnson", avatar: Avatar2 },
  { id: 3, name: "William Harris", avatar: Avatar3 },
  { id: 4, name: "Emma Wilson", avatar: Avatar4 },
  { id: 5, name: "Matthew Brooks", avatar: Avatar5 },
  { id: 6, name: "Sophia Davis", avatar: Avatar6 },
  { id: 7, name: "Andrew Parker", avatar: Avatar7 },
  { id: 8, name: "Olivia Miller", avatar: Avatar8 },
];

// Styled Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      sx: {
        zIndex: 2020,
      },
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const SharePopup = ({
  open,
  onClose,
  shareUrl = "https://dasbazi.com/toy/yR14Cb42mbVnDf",
  contentTitle,
  onEmailShareSuccess
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Determine content type from URL
  const getContentType = () => {
    if (shareUrl.includes("/toy/")) return "toy item";
    if (shareUrl.includes("/story/")) return "story";
    if (shareUrl.includes("/video/")) return "video";
    return "item";
  };
  const [emailData, setEmailData] = useState({
    email: "",
    note: "",
  });
  const [emailErrors, setEmailErrors] = useState({
    email: "",
    note: "",
  });
  const scrollContainerRef = useRef(null);

  const [shareViaEmail] = useMutation(SHARE_VIA_EMAIL_MUTATION, {
    awaitRefetchQueries: true,
  });

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
  };

  const resetEmailState = () => {
    setShowEmailForm(false);
    setShowSuccessMessage(false);
    setEmailData({ email: "", note: "" });
    setEmailErrors({ email: "", note: "" });
  };

  const handleEmailClick = () => {
    setShowEmailForm(true);
  };

  const handleEmailChange = (field, value) => {
    setEmailData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (emailErrors[field]) {
      setEmailErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleShareEmail = async() => {
    let hasError = false;
    const errors = { email: "", note: "" };

    if (!emailData.email.trim()) {
      errors.email = "Please enter recipient’s email address.";
      hasError = true;
    } else if (!validateEmail(emailData.email)) {
      errors.email = "Please enter a valid email address";
      hasError = true;
    }

    if (!emailData.note.trim()) {
      errors.note = "Please enter a note.";
      hasError = true;
    }

    if (hasError) {
      setEmailErrors(errors);
      return;
    }

    // Send the email via API
    const { data, error } = await shareViaEmail({
      variables: {
        email: emailData.email,
        note: emailData.note,
        url: shareUrl,
        contentType: getContentType(),
        contentTitle: contentTitle,
      },
    });

    if (error) {
      console.log("Error in sending email of toy share", error, data);
      setShowSuccessMessage(false);
    } else {
      console.log("Success in sending email of toy share");
      setShowSuccessMessage(true);
      if (onEmailShareSuccess) {
        onEmailShareSuccess();
      };
    };
  };

  const handleSuccessOk = () => {
    setShowSuccessMessage(false);
    setShowEmailForm(false);
    setEmailData({ email: "", note: "" });
    setEmailErrors({ email: "", note: "" });
    onClose();
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth,
      } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const filteredUsers = SAMPLE_USERS.filter((user) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      sx={{
        zIndex: 2010,
      }}
      PaperProps={{
        sx: {
          width: "585px",
          height: "510px",
          maxWidth: "95vw",
          borderRadius: "24px",
          position: "relative",
          overflow: "hidden",
          "@media (max-width: 856px)": {
            maxWidth: "95vw",
            height: "610px",
            maxHeight: "95vh",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShareOutlined sx={{ fontSize: "16px", color: "#666" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Share
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            resetEmailState();
            onClose();
          }}
          sx={{
            color: "#666",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: "28px" }} />
        </IconButton>
      </Box>
      <Divider />

      {/* Dialog Content */}
      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* Search Bar - Hidden for now */}
        {false && !showEmailForm && !showSuccessMessage && (
          <>
            <TextField
              fullWidth
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                px: 2,
                my: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  height: "46px",
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#999",
                },
              }}
            />
            <Divider />
          </>
        )}

        {/* Conditional Content: Email Form, Success Message, or Users Grid */}
        {showSuccessMessage ? (
          /* Success Message */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "370px",
              maxHeight: "370px",
              "@media (max-width: 856px)": {
                minHeight: "470px",
                maxHeight: "470px",
              },
              px: 4,
              py: 6,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                color: "#666",
                textAlign: "center",
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              You successfully shared a {getContentType()} with{" "}
              <Box component="span" sx={{ fontWeight: "600", color: "#333" }}>
                {emailData.email}
              </Box>
              .
            </Typography>
            <Button
              onClick={handleSuccessOk}
              variant="contained"
              sx={{
                backgroundColor: "#EF5658",
                color: "white",
                borderRadius: "4px",
                py: 1.5,
                height: "36px",
                fontSize: "14px",
                fontWeight: "medium",
                textTransform: "uppercase",
                minWidth: "140px",
                "&:hover": {
                  backgroundColor: "#D33338",
                },
              }}
            >
              OK
            </Button>
          </Box>
        ) : showEmailForm ? (
          /* Email Form */
          <Box
            sx={{
              px: 3,
              pt: 2,
              minHeight: "370px",
              maxHeight: "370px",
              overflowY: "auto",
              "@media (max-width: 856px)": {
                minHeight: "470px",
                maxHeight: "470px",
              },
            }}
          >
            {/* Email Field */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email"
                required
                placeholder="Enter recipient email"
                value={emailData.email}
                onChange={(e) => handleEmailChange("email", e.target.value)}
                error={!!emailErrors.email}
                helperText={emailErrors.email}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "56px",
                  },
                  "& fieldset": {
                    borderColor: emailErrors.email
                      ? "#ef5658"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#ef5658 !important",
                  },
                  "& .MuiFormLabel-asterisk": {
                    color: "#ef5658 !important",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: emailErrors.email
                      ? "#ef5658"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ef5658",
                    borderWidth: "1px",
                  },
                  "& .MuiFormHelperText-root": {
                    marginLeft: 0,
                    marginTop: "4px",
                    fontSize: "12px",
                  },
                }}
              />
            </Box>

            {/* Note Field */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Your note"
                required
                multiline
                rows={4}
                placeholder="Enter your message"
                value={emailData.note}
                onChange={(e) => handleEmailChange("note", e.target.value)}
                error={!!emailErrors.note}
                helperText={emailErrors.note}
                sx={{
                  "& fieldset": {
                    borderColor: emailErrors.note
                      ? "#ef5658"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                  "& .MuiInputLabel-asterisk": {
                    color: "#ef5658 !important",
                  },
                  "& .MuiFormLabel-asterisk": {
                    color: "#ef5658 !important",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: emailErrors.note
                      ? "#ef5658"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ef5658",
                    borderWidth: "1px",
                  },
                  "& .MuiFormHelperText-root": {
                    marginLeft: 0,
                    marginTop: "4px",
                    fontSize: "12px",
                  },
                }}
              />
            </Box>

            {/* URL Field (Read-only) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="URL"
                value={shareUrl}
                InputProps={{
                  disabled: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "56px",
                    cursor: "default",
                  },
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "& .MuiInputBase-input": {
                    color: "#999",
                    cursor: "default",
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              />
            </Box>

            {/* Share Button */}
            <Box sx={{ display: "flex", justifyContent: "center",mb:2 }}>
              <Button
                onClick={handleShareEmail}
                variant="contained"
                sx={{
                  backgroundColor: "#EF5658",
                  color: "white",
                  borderRadius: "4px",
                  py: 1.5,
                  height: "36px",
                  fontSize: "14px",
                  fontWeight: "medium",
                  textTransform: "uppercase",
                  minWidth: "140px",
                  "&:hover": {
                    backgroundColor: "#D33338",
                  },
                }}
              >
                SHARE
              </Button>
            </Box>
          </Box>
        ) : (
          /* Simple Message - No user selection needed */
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
              maxHeight: "300px",
              px: 3,
              "@media (max-width: 856px)": {
                minHeight: "400px",
                maxHeight: "400px",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Click social icon to share.
            </Typography>
          </Box>
        )}

        {/* Hidden Users Grid - For future use */}
        {false && (
          <Box
            sx={{
              minHeight: "300px",
              maxHeight: "300px",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 1,
              mb: 1,
              mt: 1,
              px: 3,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555",
              },
              "@media (max-width: 856px)": {
                gridTemplateColumns: "repeat(2, 1fr)",
                minHeight: "400px",
                maxHeight: "400px",
              },
            }}
          >
            {filteredUsers.map((user) => {
              const isSelected = selectedUsers.includes(user.id);
              return (
                <Box
                  key={user.id}
                  onClick={() => handleUserToggle(user.id)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    position: "relative",
                    border: "1px solid transparent",
                    borderRadius: "16px",
                    width: "125px",
                    height: "144px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #4CAF50",
                    },
                  }}
                >
                  {/* User Avatar */}
                  <Box
                    sx={{
                      position: "relative",
                      mb: 1,
                      mt: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src={user.avatar}
                      alt={user.name}
                      sx={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    {/* Check Icon */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: -5,
                          backgroundColor: "white",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: "20px",
                            color: "#4CAF50",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  {/* User Name */}
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      textAlign: "center",
                      fontWeight: "500",
                      px: 1,
                    }}
                  >
                    {user.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Share Link - Hide during email process */}
        {!showEmailForm && !showSuccessMessage && (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              py: 1.5,
              px: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "46px",
                backgroundColor: "white",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#666",
                  px: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {shareUrl}
              </Typography>
              <Box
                onClick={handleCopyLink}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1.5,
                  px: 2,
                  cursor: "pointer",
                  height: "100%",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: "20px", color: "#666" }} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Social Media Icons */}
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            position: "relative",
            px: 2,
            my: 0,
          }}
        >
          {/* Left Arrow */}
          {showLeftArrow && (
            <Box
              onClick={scrollLeft}
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  opacity: 0.7,
                },
                "@media (min-width: 520px)": {
                  display: "none",
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: "28px", color: "#666" }} />
            </Box>
          )}

          {/* Scrollable Container */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 1.5,
              flexWrap: "wrap",
              "@media (max-width: 856px)": {
                flexWrap: "nowrap",
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              },
            }}
          >
            {/* Instagram - Opens Instagram in new tab (no direct share API) */}
            <StyledTooltip title="Share on Instagram">
              <IconButton
                onClick={() => {
                  resetEmailState();
                  window.open("https://www.instagram.com/", "_blank");
                }}
                sx={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#FFE8EC",
                  border: "2px solid transparent",
                  "&:hover": {
                    backgroundColor: "#FFE8EC",
                    border: "1px solid #E4405F",
                  },
                }}
              >
                <InstagramIcon sx={{ fontSize: "24px", color: "#E4405F" }} />
              </IconButton>
            </StyledTooltip>

            {/* WhatsApp */}
            <StyledTooltip title="Share on WhatsApp">
              <WhatsappShareButton
                url={shareUrl}
                title="Check out this toy!"
                onClick={resetEmailState}
              >
                <IconButton
                  sx={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#E8F8EF",
                    border: "2px solid transparent",
                    "&:hover": {
                      backgroundColor: "#E8F8EF",
                      border: "1px solid #25D366",
                    },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: "24px", color: "#25D366" }} />
                </IconButton>
              </WhatsappShareButton>
            </StyledTooltip>

            {/* Facebook */}
            <StyledTooltip title="Share on Facebook">
              <FacebookShareButton
                url={shareUrl}
                quote="Check out this toy!"
                onClick={resetEmailState}
              >
                <IconButton
                  sx={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#E7F3FF",
                    border: "2px solid transparent",
                    "&:hover": {
                      backgroundColor: "#E7F3FF",
                      border: "1px solid #1877F2",
                    },
                  }}
                >
                  <FacebookIcon sx={{ fontSize: "24px", color: "#1877F2" }} />
                </IconButton>
              </FacebookShareButton>
            </StyledTooltip>

            {/* X (Twitter) */}
            <StyledTooltip title="Share on X">
              <TwitterShareButton
                url={shareUrl}
                title="Check out this toy!"
                onClick={resetEmailState}
              >
                <IconButton
                  sx={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#E8E8E8",
                    border: "2px solid transparent",
                    "&:hover": {
                      backgroundColor: "#E8E8E8",
                      border: "1px solid #000000",
                    },
                  }}
                >
                  <XIcon sx={{ fontSize: "24px", color: "#000000" }} />
                </IconButton>
              </TwitterShareButton>
            </StyledTooltip>

            {/* TikTok - Opens TikTok in new tab (no direct share API) */}
            <StyledTooltip title="Share on TikTok">
              <IconButton
                onClick={() => {
                  resetEmailState();
                  window.open("https://www.tiktok.com/", "_blank");
                }}
                sx={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#E0F7FF",
                  border: "2px solid transparent",
                  "&:hover": {
                    backgroundColor: "#E0F7FF",
                    border: "1px solid #00D4FF",
                  },
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  sx={{ width: "20px", height: "20px" }}
                >
                  <path
                    fill="#00D4FF"
                    d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
                  />
                </Box>
              </IconButton>
            </StyledTooltip>

            {/* LinkedIn */}
            <StyledTooltip title="Share on LinkedIn">
              <LinkedinShareButton
                url={shareUrl}
                title="Check out this toy!"
                onClick={resetEmailState}
              >
                <IconButton
                  sx={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#E7F3FF",
                    border: "2px solid transparent",
                    "&:hover": {
                      backgroundColor: "#E7F3FF",
                      border: "1px solid #0A66C2",
                    },
                  }}
                >
                  <LinkedInIcon sx={{ fontSize: "24px", color: "#0A66C2" }} />
                </IconButton>
              </LinkedinShareButton>
            </StyledTooltip>

            {/* Email */}
            <StyledTooltip title="Send Email">
              <IconButton
                onClick={handleEmailClick}
                sx={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#FFEAEA",
                  border: "2px solid transparent",
                  "&:hover": {
                    backgroundColor: "#FFEAEA",
                    border: "1px solid #EA4335",
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: "24px", color: "#EA4335" }} />
              </IconButton>
            </StyledTooltip>
          </Box>

          {/* Right Arrow */}
          {showRightArrow && (
            <Box
              onClick={scrollRight}
              sx={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  opacity: 0.7,
                },
                "@media (min-width: 520px)": {
                  display: "none",
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: "28px", color: "#666" }} />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SharePopup;
